import React, { useEffect, useState } from 'react';
import { store } from '../../redux/oldStore';
import { toast } from 'react-toastify';
import FileDownload from 'js-file-download';
import { csvLine, splitCSV } from '../../utils/Tools';
import { ColumnData } from '../../utils/CommonTypes/CommonTypes';
import { ServerResponse } from '../../utils/Server';
import Modal from '../Modal/Modal';
import Icon, { IconType } from '../Icon/Icon';
import { UploadType } from './ParticipantsUpload';

interface IColumnMap {
  index: number;
  data: ColumnData;
  key: number;
}

interface IFlexImportFlow {
  PublicId: string;
  Name: string;
  IsRegistrationFlow: boolean;
}

interface IFlexImportOption {
  Name: string;
  Value: string;
}

interface IFlexImportField {
  Id: number;
  PublicId: string;
  Name: string;
  Label: string;
  Required: boolean;
  ContentSubType: string;
  Type: number;
  TypeName: string;
  Options: IFlexImportOption[];
}

interface IFlexImportInfo {
  Flows: IFlexImportFlow[];
  Flow?: IFlexImportFlow;
  Fields?: IFlexImportField[];
}

interface IFlexImportResult {
  AllImported: boolean;
  FailedToImport: string[];
  TotalImported: number;
}

interface IFlexColumn extends ColumnData {
  required?: boolean;
  aliases?: string[];
}

const normalizeHeader = (value: string) => (value || '').toLowerCase().replace(/[\s\*\/_-]/g, '');

const FlexFlowUpload = () => {
  const [flows, setFlows] = useState<IFlexImportFlow[]>([]);
  const [flowId, setFlowId] = useState('');
  const [selectedFlow, setSelectedFlow] = useState<IFlexImportFlow | undefined>();
  const [fields, setFields] = useState<IFlexImportField[]>([]);
  const [columns, setColumns] = useState<IFlexColumn[]>([]);
  const [fileText, setFileText] = useState<string[][]>([]);
  const [headersIncluded, setHeadersIncluded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [parsingErrors, setParsingErrors] = useState<string[] | null>([]);
  const [uploadType, setUploadType] = useState<UploadType>('comma');
  const [showMapColumns, setShowMapColumns] = useState(false);
  const [columnMapping, setColumnMapping] = useState<Record<number, IColumnMap | undefined>>({});
  const [mappedColumns, setMappedColumns] = useState<IFlexColumn[]>([]);
  const [pasteText, setPasteText] = useState('');
  const [markFinished, setMarkFinished] = useState(false);
  const [sendEmails, setSendEmails] = useState(false);

  useEffect(() => {
    store.server.getApi<ServerResponse<IFlexImportInfo>>('../Import/FlexFlowInfo').then((x) => {
      if (x.Success) {
        setFlows(x.Value?.Flows ?? []);
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  }, []);

  useEffect(() => {
    if (!flowId) {
      setSelectedFlow(undefined);
      setFields([]);
      setColumns([]);
      resetFile();
      return;
    }

    store.server.getApi<ServerResponse<IFlexImportInfo>>(`../Import/FlexFlowInfo?flowId=${flowId}`).then((x) => {
      if (x.Success) {
        const nextFields = x.Value?.Fields ?? [];
        setSelectedFlow(x.Value?.Flow);
        setFields(nextFields);
        setColumns(
          nextFields.map((field) => ({
            id: field.PublicId,
            value: field.Label || field.Name,
            required: field.Required,
            aliases: [field.Name, field.Label, field.TypeName].filter((v): v is string => !!v),
          })),
        );
        resetFile();
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  }, [flowId]);

  const resetFile = () => {
    setFileText([]);
    setHeadersIncluded(false);
    setLoaded(false);
    setParsingErrors([]);
    setShowMapColumns(false);
    setColumnMapping({});
    setMappedColumns([]);
    setPasteText('');
  };

  const findColumn = (header: string): IFlexColumn | undefined => {
    const normalized = normalizeHeader(header);
    if (!normalized) return undefined;
    return columns.find((col) => {
      if (normalizeHeader(col.value) === normalized || normalizeHeader(col.id) === normalized) return true;
      return col.aliases?.some((alias) => normalizeHeader(alias) === normalized);
    });
  };

  const applyMappedRows = (rows: string[][], mapping: Record<number, IColumnMap | undefined>, hasHeaders: boolean) => {
    const maps = Object.keys(mapping)
      .map((key) => mapping[+key])
      .filter((m): m is IColumnMap => !!m?.data);

    if (!maps.length) {
      toast.error('Map at least one column before processing the file.', { autoClose: false });
      return;
    }

    const ordered = columns.map((col) => maps.find((m) => m.data.id === col.id)).filter((m): m is IColumnMap => !!m);
    const data = rows.map((row) => ordered.map((m) => row[m.key] ?? ''));

    setMappedColumns(ordered.map((m) => m.data as IFlexColumn));
    setFileText(data);
    setHeadersIncluded(hasHeaders);
    setShowMapColumns(false);
    setParsingErrors(null);
  };

  const processMappedFile = () => {
    applyMappedRows(fileText, columnMapping, headersIncluded);
  };

  const tryAutoMap = (rows: string[][]): boolean => {
    if (!rows.length || !columns.length) return false;
    const headerValues = rows[0];
    const mapping: Record<number, IColumnMap | undefined> = {};
    const usedIds = new Set<string>();
    let matched = 0;

    headerValues.forEach((header, i) => {
      const col = findColumn(header);
      if (!col || usedIds.has(col.id)) return;
      usedIds.add(col.id);
      mapping[i] = { index: columns.indexOf(col), data: col, key: i };
      matched++;
    });

    setColumnMapping(mapping);
    if (matched > 0) setHeadersIncluded(true);

    if (matched === headerValues.length && matched > 0) {
      applyMappedRows(rows, mapping, true);
      return true;
    }
    return false;
  };

  const processTextFile = (text: string) => {
    if (!flowId) {
      toast.error('Select a flex flow first.', { autoClose: false });
      return;
    }
    if (!columns.length) {
      toast.error('This flow has no importable fields.', { autoClose: false });
      return;
    }

    const lines: string[][] = [];
    const formattedText = text.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
    const rawLines = formattedText.split('\n');

    for (let i = 0; i < rawLines.length; i++) {
      const l = rawLines[i];
      if (l.length === 0) continue;

      const lineIndexes = csvLine(rawLines, i);
      let line = rawLines[lineIndexes[0]];
      for (let li = 1; li < lineIndexes.length; li++) {
        i = lineIndexes[li];
        line += '<br/>' + rawLines[lineIndexes[li]];
      }

      const lineValues = splitCSV(line, uploadType).map((v) => v.replace(/(\<br\/\>)/g, '\r\n'));
      lines.push(lineValues);
    }

    setLoaded(false);
    setColumnMapping({});
    setMappedColumns([]);
    setHeadersIncluded(false);

    if (!lines.length) {
      setFileText([]);
      setParsingErrors(['The file did not contain any rows.']);
      return;
    }

    if (tryAutoMap(lines)) return;

    const firstLooksLikeHeader = lines[0].some((cell) => !!findColumn(cell));
    if (firstLooksLikeHeader) setHeadersIncluded(true);

    setFileText(lines);
    setParsingErrors(null);
    setShowMapColumns(true);
  };

  const fileSelected = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev: any) => processTextFile(ev.target.result);
    reader.readAsText(file, 'utf-8-sig');
  };

  const load = () => {
    const realText = headersIncluded ? fileText.slice(1) : fileText;
    store.server
      .postApi<ServerResponse<IFlexImportResult>>('../Import/LoadFlexFlowRecords', {
        FlowId: flowId,
        Data: realText,
        Columns: mappedColumns.map((x) => x.id),
        MarkFinished: markFinished,
        SendEmails: sendEmails,
      })
      .then((x) => {
        if (x.Success) {
          if (!x.Value.AllImported) {
            toast.warning(
              <>
                We were not able to import every row.
                <ul>
                  {(x.Value.FailedToImport ?? []).map((f, i) => (
                    <li key={`fti-${i}`}>{f}</li>
                  ))}
                </ul>
                Click to close this window
              </>,
              { autoClose: false },
            );
            setParsingErrors(x.Value.FailedToImport);
            setLoaded(true);
          } else {
            toast.success(`Imported ${x.Value.TotalImported} flex record${x.Value.TotalImported === 1 ? '' : 's'}.`);
            setLoaded(true);
          }
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .catch((err) => {
        toast.error(String(err), { autoClose: false });
      });
  };

  const downloadTemplateClick = () => {
    if (!columns.length) {
      toast.error('Select a flex flow first.', { autoClose: false });
      return;
    }
    const delim = uploadType === 'tab' ? '\t' : ',';
    const header = columns
      .map((col) => {
        const value = col.value || col.id;
        return value.indexOf(delim) >= 0 || value.indexOf('"') >= 0 ? `"${value.replace(/"/g, '""')}"` : value;
      })
      .join(delim);
    const name = (selectedFlow?.Name || 'flex-flow').replace(/[^\w\-]+/g, '_');
    FileDownload(`${header}\r\n`, `${name}_upload_template.csv`);
  };

  const previewHeaders = mappedColumns.length ? mappedColumns.map((x) => x.value) : fileText[0] || [];

  return (
    <div className="loading-container" data-testid="import-flex-page">
      <h1>Load Flex Flow Data</h1>
      <div>
        Each flex flow has different fields. Select a flow, then upload a CSV or paste rows. Map columns to that
        flow&apos;s inputs.
        <ul>
          <li>
            <strong>Google Sheets</strong> Select File, then Download then as Comma-separated values (.csv)
          </li>
          <li>
            <strong>Excel</strong> Select File, Save as, then change type to CSV UTF-8 (*.csv)
          </li>
        </ul>
      </div>
      <div>
        <h3>Notes:</h3>
        <ul>
          <li>Every row creates a new record. Existing records are not updated or attached to existing people.</li>
          <li>File, image, and audio fields cannot be imported from CSV.</li>
          <li>Registration flows also create user accounts from mapped person fields. Duplicate username or name+email rows fail.</li>
          <li>Use semicolons to separate multi-select values.</li>
        </ul>
      </div>

      <div className="bumper-l">
        <label htmlFor="import-flex-flow">Flex Flow</label>
        <select
          id="import-flex-flow"
          data-testid="import-flex-flow"
          className="form-control"
          value={flowId}
          onChange={(e) => setFlowId(e.target.value)}>
          <option value="">Select a flow...</option>
          {flows.map((flow) => (
            <option key={flow.PublicId} value={flow.PublicId}>
              {flow.Name}
              {flow.IsRegistrationFlow ? ' (registration)' : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedFlow && (
        <div className="bumper-l">
          <h4>{selectedFlow.Name}</h4>
          {selectedFlow.IsRegistrationFlow && (
            <p>This is a registration flow. Mapped name, email, username, and other person fields will create accounts.</p>
          )}
          {columns.length === 0 && <p>This flow has no importable text fields.</p>}
          {columns.length > 0 && (
            <ul>
              {columns.map((col) => (
                <li key={col.id}>
                  {col.value}
                  {col.required ? ' (required on the form)' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loaded && selectedFlow && columns.length > 0 && (
        <div>
          <div className="flex-between">
            <h3>Load Records</h3>
            <button className="btn btn-secondary btn-xs" type="button" onClick={downloadTemplateClick} data-testid="import-flex-template">
              Download Template
            </button>
          </div>
          <div className="bumper-l">
            <label>Delimiter: </label>
            <select className="form-control" onChange={(e) => setUploadType(e.target.value as UploadType)} value={uploadType} data-testid="import-flex-delimiter">
              <option value={'comma'}>Comma</option>
              <option value={'tab'}>Tab</option>
            </select>
          </div>
          <div className="bumper-l">
            <label htmlFor="import-flex-file">CSV File:</label>
            <input
              id="import-flex-file"
              data-testid="import-flex-file"
              type="file"
              accept=".csv,.txt"
              onChange={fileSelected}
              onClick={(evt: any) => (evt.target.value = null)}
            />
          </div>
          <div className="bumper-l">
            <label htmlFor="import-flex-paste">Or paste CSV / TSV:</label>
            <textarea
              id="import-flex-paste"
              data-testid="import-flex-paste"
              className="form-control"
              rows={8}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste rows here, including a header row if you have one."
            />
            <button
              type="button"
              className="btn btn-default bumper-out-l"
              data-testid="import-flex-use-paste"
              onClick={() => processTextFile(pasteText)}>
              Use pasted data
            </button>
          </div>
          <div className="bumper-l">
            <input
              type="checkbox"
              id="import-flex-finished"
              data-testid="import-flex-finished"
              checked={markFinished}
              onChange={(e) => setMarkFinished(e.target.checked)}
            />
            <label htmlFor="import-flex-finished" className="control-label">
              Mark imported records as finished
            </label>
          </div>
          <div className="bumper-l">
            <input
              type="checkbox"
              id="import-flex-emails"
              data-testid="import-flex-emails"
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
            />
            <label htmlFor="import-flex-emails" className="control-label">
              Send flow emails (welcome / notify on done)
            </label>
          </div>

          {fileText && fileText.length > 0 && (
            <div className="csv-file-preview" data-testid="import-flex-preview">
              <table>
                {!headersIncluded && (
                  <thead>
                    <tr>
                      {previewHeaders.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {fileText.map((r, ri) => (
                    <tr key={'r' + ri}>
                      {r.map((v, vi) => {
                        if (headersIncluded && ri === 0) {
                          return <th key={ri + '-' + vi}>{v}</th>;
                        }
                        return <td key={ri + '-' + vi}>{v}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {parsingErrors && parsingErrors.length > 0 && (
            <table>
              <tbody>
                {parsingErrors.map((error, index) => (
                  <tr key={index}>
                    <td>{error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loaded && fileText && fileText.length > 0 && mappedColumns.length > 0 && (
        <div className="ready-to-load-area">
          <h4>If the file contents look good, press Load</h4>
          <button type="button" className="btn btn-success" onClick={load} data-testid="import-flex-load">
            <i className="fad fa-cloud-upload"></i>
            Load
          </button>
        </div>
      )}
      {loaded && (
        <div data-testid="import-flex-done">
          <h1>Flex records loaded!</h1>
          If you need to load more records refresh your page.
        </div>
      )}

      {showMapColumns && (
        <Modal setModalOpen={setShowMapColumns} title="Map Columns to Load Data" size="l" noClose={true}>
          <div className="form-horizontal" data-testid="import-flex-map">
            <h4>
              <Icon type={IconType.mapO} />
              Map Columns to Data Points
            </h4>
            Map each column from your file to a field on {selectedFlow?.Name}. File, image, and audio fields are not
            listed.
            <div className="form-group">
              <div className="col-sm-offset-4 col-sm-8">
                <input
                  type="checkbox"
                  id="headers-flex"
                  className=""
                  onChange={(e) => setHeadersIncluded(e.target.checked)}
                  defaultChecked={headersIncluded}
                />
                <label htmlFor="headers-flex" className="control-label">
                  First row are data headers
                </label>
              </div>
            </div>
            {fileText &&
              fileText.length > (headersIncluded ? 1 : 0) &&
              fileText[0].map((x, i) => (
                <div className="form-group" key={i}>
                  <label className="col-sm-4 control-label" htmlFor={`map-column-${i}-flex`}>
                    {headersIncluded ? (
                      <>
                        Column {i + 1}: <br />
                        {x}
                      </>
                    ) : (
                      <>
                        Column {i + 1}: <br />({x.substring(0, 25)})
                      </>
                    )}
                  </label>
                  <div className="col-sm-8">
                    <select
                      id={`map-column-${i}-flex`}
                      data-testid={`import-flex-map-${i}`}
                      className="form-control"
                      defaultValue={columnMapping[i] != null ? String(columnMapping[i]?.index) : '-1'}
                      onChange={(e) => {
                        const index = +e.target.value;
                        setColumnMapping((prev) => {
                          const mapping = { ...prev };
                          mapping[i] = index > -1 ? { index, data: columns[index], key: i } : undefined;
                          return mapping;
                        });
                      }}>
                      <option value="-1">Select data point...</option>
                      {columns.map((c, ci) => (
                        <option key={`flex-${i}-${ci}`} value={ci}>
                          {c.value}
                          {c.required ? ' (required)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            <div className="form-group">
              <div className="col-sm-4"></div>
              <div className="col-sm-8">
                <button type={'button'} className="btn btn-secondary" onClick={processMappedFile} data-testid="import-flex-process">
                  <Icon type={IconType.upload} /> Process File
                </button>
                <button
                  type={'button'}
                  className="btn btn-default"
                  onClick={() => {
                    setShowMapColumns(false);
                  }}>
                  <Icon type={IconType.close} /> Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default FlexFlowUpload;
